import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { esDevice, esDeviceParams, esDeviceWatch } from './EsDevice';

// Google Analytics 4, prod only, and only because prod's build is handed an ID.
// GA_MEASUREMENT_ID is a build-time value like LD_CLIENT_ID (webpack.config.js):
// blank means no script is added and no request is made, which is what every
// other environment, CI, jest and AQA build gets. A G- ID is public by design.
//
// The pattern test is not decoration: the ID goes into a script URL, so a
// malformed value is refused rather than interpolated.
const GA_PATTERN = /^G-[A-Z0-9]{4,20}$/;

// A browser driven by automation counts for nothing. The prod check after every
// prod deploy runs AQA against escapade.gg, and each of its logins clears the _ga
// cookie, so one check minted ~650 GA users -- 95% of the property's first month.
// Selenium and headless crawlers both set navigator.webdriver.
export const esAnalyticsId = () => {
    if ( typeof navigator !== 'undefined' && navigator.webdriver ) return '';
    const aId = process.env.GA_MEASUREMENT_ID || '';
    return GA_PATTERN.test( aId ) ? aId : '';
};

// What GA is told the page is called. document.title carries the version and the
// signed-in nickname (EsPageTitle), and gtag reads it on its own unless told
// otherwise.
export const ES_ANALYTICS_TITLE = 'Escapade';

// The path with every route parameter, and any splat, replaced by its name. Query
// and hash never get here: invite codes, opt-out tokens and mailing tokens ride
// in them, and pack and version keys ride in the path itself.
export const esAnalyticsPath = ( pPathname, pParams ) => {
    const aNames = Object.keys( pParams || {} ).filter( ( pName ) => pName !== '*' );
    const aSegments = pPathname.split( '/' ).map( ( pSegment ) => {
        let aDecoded = pSegment;
        try {
            aDecoded = decodeURIComponent( pSegment );
        } catch {
            return ':unreadable';
        }
        const aName = aNames.find( ( pName ) => pParams[ pName ] === aDecoded );
        return aName ? ':' + aName : pSegment;
    } );

    const aSplat = pParams && pParams[ '*' ];
    if ( aSplat ) {
        const aCount = aSplat.split( '/' ).length;
        aSegments.splice( aSegments.length - aCount, aCount, '*' );
    }
    return aSegments.join( '/' );
};

// Campaign attribution is the one query GA needs. Every other parameter is
// dropped: invite codes and opt-out tokens ride there.
const UTM_KEYS = [ 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content' ];

export const esAnalyticsQuery = ( pSearch ) => {
    const aFrom = new URLSearchParams( pSearch || '' );
    const aTo = new URLSearchParams();
    UTM_KEYS.forEach( ( pKey ) => {
        if ( aFrom.has( pKey ) ) aTo.set( pKey, aFrom.get( pKey ) );
    } );
    const aText = aTo.toString();
    return aText ? '?' + aText : '';
};

let sLoaded = '';
let sPrevious = '';

// gtag.js wants the `arguments` object itself on dataLayer, not an array.
const gtag = function () {
    window.dataLayer.push( arguments );
};

// EsDevice's snapshot as parameters on every event that follows, page_view included.
// GA4 caps an event at 25 parameters, and the full snapshot plus page_view's own four
// is exactly 25, so the fields GA already collects stay out: its Screen resolution,
// Browser, Operating system, Language, and Device category. New Relic gets all of them.
// A null is left out rather than sent as an empty value.
const GA_COLLECTS = [ 'screen_width', 'screen_height', 'browser_family', 'os_family', 'browser_language', 'device_kind' ];

const setDevice = ( pDevice ) => {
    const aParams = Object.fromEntries( Object.entries( esDeviceParams( pDevice ) )
        .filter( ( [ pName, pValue ] ) => pValue !== null && pValue !== undefined && !GA_COLLECTS.includes( pName ) ) );
    gtag( 'set', aParams );
};

// A resize sends nothing on its own: it changes what the next event carries.
const watchDevice = ( pDevice ) => {
    if ( sLoaded && window.dataLayer ) setDevice( pDevice );
};

const load = ( pId ) => {
    if ( sLoaded === pId && window.dataLayer ) return;
    sLoaded = pId;

    window.dataLayer = window.dataLayer || [];
    // Consent mode v2 with everything denied, and no banner to lift it (Tim,
    // 2026-10-23): GA writes and reads no cookies and sends cookie-less pings, so
    // page counts survive without anything stored in the browser. It has to be
    // the first command; gtag applies a default only to what follows it.
    gtag( 'consent', 'default', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
    } );
    gtag( 'js', new Date() );
    gtag( 'set', { page_title: ES_ANALYTICS_TITLE } );
    // send_page_view off: the router sends one per route change, with a path that
    // has been through esAnalyticsPath. The two signals flags are Google's
    // advertising features; Escapade does not want either.
    gtag( 'config', pId, {
        send_page_view: false,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
    } );

    const aScript = document.createElement( 'script' );
    aScript.async = true;
    aScript.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent( pId );
    document.head.appendChild( aScript );
    esDeviceWatch( watchDevice );
};

// The first page's referrer is whatever the browser reports, which may be another
// site's URL with a query of its own; GA gets its origin and path only.
const firstReferrer = () => {
    try {
        const aUrl = new URL( document.referrer );
        return aUrl.origin + aUrl.pathname;
    } catch {
        return '';
    }
};

export const esAnalyticsPageView = ( pPath, pQuery = '' ) => {
    const aId = esAnalyticsId();
    if ( !aId ) return;
    load( aId );

    const aLocation = window.location.origin + pPath + pQuery;
    // Read per page view: the first beats EsThemeContext's effect, and a route change
    // is the cheapest moment to catch anything a listener missed.
    setDevice( esDevice() );
    gtag( 'event', 'page_view', {
        page_location: aLocation,
        page_path: pPath,
        page_title: ES_ANALYTICS_TITLE,
        page_referrer: sPrevious || firstReferrer(),
    } );
    sPrevious = aLocation;
};

/**
 * One page view per route change; renders nothing, and does nothing at all
 * without a measurement ID. Effect-only like EsScrollToTop beside it in EsParent.
 */
export default function EsAnalytics() {
    const { pathname, search } = useLocation();
    const aParams = useParams();
    const aPath = esAnalyticsPath( pathname, aParams );
    const aQuery = esAnalyticsQuery( search );

    useEffect( () => {
        esAnalyticsPageView( aPath, aQuery );
    }, [ aPath, aQuery ] );

    return null;
}
